import { defineStore } from 'pinia'

import type { School } from '#payload-types'

export const useUIStore = defineStore('ui', () => {
  const route = useRoute()

  const showMenu = ref(false)
  const mountedSchool = ref<School | null>(null)
  // The school named by ?school=, branded or not, so pages can prefill from it
  // without the themed treatment mountedSchool triggers.
  const querySchool = ref<School | null>(null)

  const loadSchool = async () => {
    if (route.query.school) {
      const school = await usePayloadSchool(route.query.school as string)

      // An unresolvable ?school= is just a missing prefill — the page it's on stands on its
      // own, so it renders as though no school had been named.
      if (!school.value) {
        return
      }

      querySchool.value = school.value

      // Schools without a custom page have no theme colour or logo, so they
      // fall through to the standard, unbranded pledge page.
      if (school.value.hasCustomPage) {
        mountedSchool.value = school.value
      }
    }
  }

  const showNewsletterSignupModal = ref(false)
  const showRegistrationModal = ref(false)
  const showPreRegistrationModal = ref(false)
  // Kept here rather than on the card, which is rebuilt whenever its school page is
  // revisited, so a closed card stays closed until the next reload.
  const dismissedSaSessionPopups = ref<School['id'][]>([])

  // The SA event modals are opened from a card on the page behind them, so leaving one open
  // across a route change would strand it over unrelated content. Both flags are cleared:
  // the interest modal is mounted on the school and event pages alike, so a stale flag would
  // reopen it the moment the next SA page mounts.
  watch(
    () => route.path,
    () => {
      showRegistrationModal.value = false
      showPreRegistrationModal.value = false
    },
  )

  return {
    mountedSchool,
    querySchool,
    showNewsletterSignupModal,
    showRegistrationModal,
    showPreRegistrationModal,
    dismissedSaSessionPopups,
    showMenu,
    loadSchool,
  }
})
